import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getEstudo } from "@/lib/estudo";
import { BancaGrid } from "@/components/BancaGrid";
import { PremiumLimitModal } from "@/components/PremiumLimitModal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Infinity as InfinityIcon, Sparkles, Bot, SlidersHorizontal } from "lucide-react";
import { QUESTOES } from "@/constants/testIds/vc";

const Select = ({ value, onChange, options, testid, placeholder, disabled }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
    className="w-full min-w-0 max-w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50 sm:w-auto">
    <option value="Todas">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function QuestoesPage() {
  const { user } = useAuth();
  const premium = user?.is_premium;
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [filters, setFilters] = useState(() => {
    const est = getEstudo();
    const params = new URLSearchParams(window.location.search);
    return { banca: est.banca || "Todas", concurso: est.concurso || "Todas",
             disciplina: params.get("disciplina") || "Todas", assunto: "Todas" };
  });
  const [facets, setFacets] = useState({ concursos: [], disciplinas: [], assuntos: [] });
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitMsg, setLimitMsg] = useState("");
  const [prof, setProf] = useState({ open: false, text: "", loading: false });

  const q = questions[idx];

  const askProfessor = async () => {
    if (!q) return;
    if (!premium) {
      window.location.href = "/premium";
      return;
    }
    setProf({ open: true, text: "", loading: true });
    try {
      const { data } = await api.post("/ia/professor", { question_id: q.id, mode: "explicar" });
      setProf({ open: true, text: data.resposta, loading: false });
    } catch (e) {
      setProf({ open: true, text: formatError(e.response?.data?.detail) || "Erro ao consultar o Professor IA.", loading: false });
    }
  };

  const loadUsage = useCallback(() => {
    api.get("/questions/daily-usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const loadFacets = useCallback((f) => {
    api.get("/questions/facets", { params: { banca: f.banca, concurso: f.concurso, disciplina: f.disciplina } })
      .then((r) => setFacets({ concursos: r.data.concursos || [], disciplinas: r.data.disciplinas || [], assuntos: r.data.assuntos || [] }))
      .catch(() => {});
  }, []);

  const loadQuestions = useCallback((f) => {
    api.get("/questions/random", {
      params: {
        count: 10,
        banca: f.banca,
        concurso: f.concurso,
        disciplina: f.disciplina,
        assunto: f.assunto,
      },
    })
      .then((r) => { setQuestions(r.data.questions || []); setIdx(0); setSelected(null); setResult(null); })
      .catch((e) => {
        if (e.response?.status === 402) {
          setLimitMsg(formatError(e.response.data?.detail));
          setLimitOpen(true);
          loadUsage();
        }
      });
  }, [loadUsage]);

  useEffect(() => {
    loadFacets(filters);
    loadQuestions(filters);
    loadUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascading, dependent filters: BANCA -> CONCURSO -> DISCIPLINA -> ASSUNTO
  const apply = (patch) => {
    const f = { ...filters, ...patch };
    if ("banca" in patch) { f.concurso = "Todas"; f.disciplina = "Todas"; f.assunto = "Todas"; }
    if ("concurso" in patch) { f.disciplina = "Todas"; f.assunto = "Todas"; }
    if ("disciplina" in patch) { f.assunto = "Todas"; }
    setFilters(f);
    loadFacets(f);
    loadQuestions(f);
  };

  const submit = async () => {
    if (!selected || result) return;
    setLoading(true);
    try {
      const { data } = await api.post("/questions/answer", { question_id: q.id, selected });
      setResult(data); setUsage(data.daily);
    } catch (e) {
      if (e.response?.status === 402) { setLimitMsg(formatError(e.response.data?.detail)); setLimitOpen(true); loadUsage(); }
      else { setLimitMsg(formatError(e.response?.data?.detail)); setLimitOpen(true); }
    } finally { setLoading(false); }
  };

  const next = () => {
    const dailyCount = usage?.count ?? 0;
    const dailyLimit = usage?.limit ?? 10;

    if (!premium && dailyCount >= dailyLimit) {
      setLimitMsg("Você atingiu o limite de 10 questões grátis hoje. Volte amanhã ou assine o Premium para continuar.");
      setLimitOpen(true);
      return;
    }

    if (idx >= questions.length - 1) {
      if (premium) {
        loadQuestions(filters);
      } else {
        setLimitMsg("Você atingiu o limite de 10 questões grátis hoje. Volte amanhã ou assine o Premium para continuar.");
        setLimitOpen(true);
      }
      return;
    }

    setSelected(null);
    setResult(null);
    setIdx((i) => i + 1);
  };

  return (
    <div data-testid={QUESTOES.page} className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-white">Questões</h1>
          <p className="text-sm text-slate-400">Escolha a banca e refine os filtros. Gabarito comentado na hora.</p>
        </div>
        <div data-testid={QUESTOES.counterBadge}
          className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-bold ${
            premium ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
          {premium ? <><InfinityIcon className="h-4 w-4" /> Ilimitado</> : <>{usage?.count ?? 0}/{usage?.limit ?? 10} questões hoje</>}
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Banca</p>
        <BancaGrid value={filters.banca} onSelect={(b) => apply({ banca: b })} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-slate-500" />
        <Select value={filters.concurso} onChange={(v) => apply({ concurso: v })} options={facets.concursos} testid="questoes-filter-concurso" placeholder="Todos os concursos" />
        <Select value={filters.disciplina} onChange={(v) => apply({ disciplina: v })} options={facets.disciplinas} testid={QUESTOES.filterDisciplina} placeholder="Todas as disciplinas" />
        <Select value={filters.assunto} onChange={(v) => apply({ assunto: v })} options={facets.assuntos} testid="questoes-filter-assunto" placeholder="Todos os assuntos" disabled={filters.disciplina === "Todas"} />
      </div>

      {!premium && usage && !usage.is_premium && (
        <div className="h-2 overflow-hidden rounded-full bg-slate-800">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, ((usage.count ?? 0) / (usage.limit ?? 10)) * 100)}%` }} />
        </div>
      )}

      {!q && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400" data-testid="questoes-empty">
          Nenhuma questão para estes filtros. Ajuste a seleção.
        </div>
      )}

      {q && (
        <div className="vc-fade-up rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6">
          <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-semibold">
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">{q.disciplina}</span>
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-400">{q.banca || q.banca_referencia}</span>
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-400">{q.orgao}</span>
            <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-400">{q.dificuldade}</span>
          </div>
          <p className="text-[15px] leading-relaxed text-slate-100">{q.enunciado}</p>
          {q.fonte === "QAPI" && (
            <p className="mt-2 text-xs text-slate-400">
              Questão fornecida por QAPI
            </p>
          )}

          <div className="mt-4 space-y-2">
            {q.options.map((opt) => {
              const isSel = selected === opt.key;
              const isCorrect = result && opt.key === result.correct_answer;
              const isWrong = result && isSel && !result.correct;
              return (
                <button key={opt.key} data-testid={`${QUESTOES.option}-${opt.key}`} disabled={!!result} onClick={() => setSelected(opt.key)}
                  className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left text-sm transition-all ${
                    isCorrect ? "border-emerald-500 bg-emerald-500/10 text-emerald-100"
                    : isWrong ? "border-red-500 bg-red-500/10 text-red-100"
                    : isSel ? "border-emerald-500 bg-emerald-500/5 text-white"
                    : "border-slate-700 bg-slate-800/50 text-slate-200 hover:border-slate-600"}`}>
                  <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-xs font-bold ${isSel || isCorrect ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"}`}>{opt.key}</span>
                  <span className="pt-0.5">{opt.text}</span>
                  {isCorrect && <CheckCircle2 className="ml-auto h-5 w-5 text-emerald-400" />}
                  {isWrong && <XCircle className="ml-auto h-5 w-5 text-red-400" />}
                </button>
              );
            })}
          </div>

          {result && (
            <div data-testid={QUESTOES.feedback} className={`mt-4 rounded-xl border p-4 ${result.correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}>
              <p className={`text-sm font-bold ${result.correct ? "text-emerald-400" : "text-red-400"}`}>
                {result.correct ? "✓ Resposta correta!" : `✗ Incorreta. Gabarito: ${result.correct_answer}`}
              </p>
              <p className="mt-1 text-sm text-slate-300"><strong>Comentário:</strong> {result.explicacao}</p>
              <button onClick={askProfessor} data-testid="questoes-professor-btn"
                className="mt-3 flex items-center gap-1.5 rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-600">
                <Sparkles className="h-3.5 w-3.5" /> Explicar com Professor IA
              </button>
            </div>
          )}

          <div className="mt-5 flex justify-end gap-3">
            {!result ? (
              <button data-testid={QUESTOES.submitButton} onClick={submit} disabled={!selected || loading}
                className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 active:scale-95 disabled:opacity-50">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Responder
              </button>
            ) : (
              <button data-testid={QUESTOES.nextButton} onClick={next}
                disabled={!premium && ((usage?.count ?? 0) >= (usage?.limit ?? 10) || idx >= questions.length - 1)}
                className="flex items-center gap-2 rounded-xl bg-slate-700 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50">
                {!premium && (usage?.count ?? 0) >= (usage?.limit ?? 10)
                  ? "10/10 concluídas hoje"
                  : "Próxima"}
                {premium || ((usage?.count ?? 0) < (usage?.limit ?? 10) && idx < questions.length - 1)
                  ? <ArrowRight className="h-4 w-4" />
                  : null}
              </button>
            )}
          </div>
        </div>
      )}

      <PremiumLimitModal open={limitOpen} onOpenChange={setLimitOpen} message={limitMsg} />

      <Dialog open={prof.open} onOpenChange={(o) => setProf((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-h-[80vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-lg">
          <DialogTitle className="flex items-center gap-2 text-indigo-300"><Bot className="h-5 w-5" /> Professor ApoiaConcursos IA</DialogTitle>
          {prof.loading ? (
            <div className="flex items-center gap-2 py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Pensando…</div>
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200" data-testid="professor-response">{prof.text}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
